<template>
  <!-- The Maya workbench drawer body for every non-sitewb payload — a port of the pre-Vue
       wbFromPayload family (wbCompare / wbReport / wbPlan / wbPlan90 / wbTask / wbDeliv / wbSite).
       Root takes over .wb-body so the foot can pin below the scroll area like the old drawer. -->
  <div class="wb-body wb-stack">
    <div class="wb-scroll">

      <div v-if="busy && !data" class="wb-load"><span class="radar2"></span><span>{{ t("wb_loading") }}</span></div>
      <div v-else-if="failed" class="wb-empty"><b>{{ t("wb_notfound") }}</b></div>

      <!-- compare: your wording vs the AI's, stacked (see .wb-cmp CSS for why 480px rules out columns) -->
      <template v-else-if="wb.type === 'compare'">
        <div class="wb-cmp a"><div class="wb-cmpl">{{ wb.leftLabel || t("wb_cmp_yours") }}</div><div class="wb-cmpb" v-html="mdLite(wb.left || '')"></div></div>
        <div class="wb-cmpsep"></div>
        <div class="wb-cmp b"><div class="wb-cmpl">{{ wb.rightLabel || t("wb_cmp_theirs") }}</div><div class="wb-cmpb" v-html="mdLite(wb.right || '')"></div></div>
      </template>

      <!-- site: same-origin GEO-site preview -->
      <template v-else-if="wb.type === 'site' && data">
        <template v-if="data.live_path">
          <div class="wb-url"><span>{{ data.live_url || data.live_path }}</span>
            <a v-if="data.live_url" :href="data.live_url" target="_blank" rel="noopener">{{ t("wb_newtab") }}</a></div>
          <iframe class="wb-frame" :src="data.live_path" loading="lazy" sandbox="allow-same-origin allow-popups" :title="t('wb_preview')"></iframe>
        </template>
        <div v-else class="wb-empty"><b>{{ t("wb_notlive_h") }}</b>{{ t("wb_notlive_s") }}
          <div v-if="(data.kind || '') === 'article'"><button type="button" class="mc-cta" @click="emit('open', { type: 'deliverable', slug: data.slug })">{{ t("art_publish") }}</button></div>
        </div>
      </template>

      <!-- deliverable / article: how-to-use rows + the full text -->
      <template v-else-if="wb.type === 'deliverable' && data">
        <div v-if="data.usage" class="wb-use">
          <div class="wb-use-r"><h5>{{ t("wb_use_h1") }}</h5><p>{{ data.usage.what || "" }}</p></div>
          <div class="wb-use-r who" :class="data.usage.mode === 'maya' ? 'mine' : 'yours'">
            <h5>{{ t("wb_use_h2") }}<span class="wb-use-bg">{{ t(data.usage.mode === "maya" ? "wb_use_maya" : "wb_use_you") }}</span></h5>
            <p>{{ data.usage.who || "" }}</p>
            <template v-if="(data.usage.places || []).length">
              <div class="wb-use-pw">{{ t("wb_use_where") }}</div>
              <ul class="wb-use-pl"><li v-for="(place, i) in data.usage.places" :key="i">{{ place }}</li></ul>
            </template>
            <div v-if="data.live_url && String(data.live_url).indexOf('http') === 0" class="wb-use-url">
              <a :href="data.live_url" target="_blank" rel="noopener">{{ data.live_url }}</a></div>
            <button v-if="data.live_path" type="button" class="wb-use-go" @click="emit('open', { type: 'site', slug: data.slug })">{{ t("wb_use_open") }}</button>
          </div>
          <div class="wb-use-r"><h5>{{ t("wb_use_h3") }}</h5><p>{{ data.usage.next || "" }}</p>
            <p v-if="data.usage_note" class="wb-use-note"><b>{{ t("wb_use_note") }}</b> {{ data.usage_note }}</p></div>
        </div>
        <div class="wb-doc art-body" v-html="bodyHtml"></div>
      </template>

      <!-- report: one section of the run, chips switch sections in place -->
      <template v-else-if="wb.type === 'report' && data">
        <div class="wb-chips">
          <button v-for="s in data.secs || []" :key="s.k" type="button" class="wb-chip" :class="{ on: s.k === sec }" @click="openSec(s.k)">{{ s.label }}</button>
        </div>
        <div class="wb-blocks">
          <BlockView v-for="(bl, i) in data.blocks || []" :key="i" :bl="bl" @section="openSec" />
          <div v-if="!(data.blocks || []).length" class="wb-empty"><b>{{ t("wb_sec_empty_h") }}</b>{{ t("wb_sec_empty_s") }}</div>
        </div>
      </template>

      <!-- plan: the live task list -->
      <template v-else-if="wb.type === 'plan' && data">
        <div v-if="!(data.tasks || []).length" class="wb-empty"><b>{{ t("wb_plan_empty") }}</b></div>
        <template v-else>
          <div class="wb-stats">{{ t("wb_plan_stats", { b: planTasks.length, x: planMine.length, y: planYours.length }) }}</div>
          <div v-if="planProposed.length" class="wb-sec">
            <div class="wb-sech warn">{{ t("wb_plan_proposed") }} ({{ planProposed.length }})</div>
            <button type="button" class="pl-go" style="margin-bottom:12px" :disabled="planBusy" @click="approveAll">{{ t("plan_approve_all") }}</button>
            <TaskCard v-for="task in planProposed" :key="task.id" :task="task" :ctx="{ output: true }" @act="onTaskAct" />
          </div>
          <div class="wb-sec"><div class="wb-sech">{{ t("wb_plan_week") }}</div>
            <template v-if="planWeek.length"><TaskCard v-for="task in planWeek" :key="task.id" :task="task" :ctx="{ output: true }" @act="onTaskAct" /></template>
            <p v-else class="wb-empty"><b>{{ t("wb_sec_empty_h") }}</b></p>
          </div>
          <div v-if="planLater.length" class="wb-sec"><div class="wb-sech">{{ t("wb_plan_later") }}</div>
            <TaskCard v-for="task in planLater" :key="task.id" :task="task" :ctx="{ output: true }" @act="onTaskAct" /></div>
          <div v-if="planDone.length" class="wb-sec"><div class="wb-sech">{{ t("wb_plan_done") }}</div>
            <TaskCard v-for="task in planDone" :key="task.id" :task="task" :ctx="{ output: true, review: true }" @act="onTaskAct" /></div>
        </template>
      </template>

      <!-- plan90: the proposal (editable) or the live 90-day arc -->
      <template v-else-if="wb.type === 'plan90' && data">
        <div v-if="!((data.stats || {}).total)" class="wb-empty"><b>{{ t("wb_p90_empty") }}</b></div>
        <template v-else>
          <div class="wb-stats">{{ t("wb_p90_stats", { b: data.stats.total, x: data.stats.maya, y: data.stats.client, z: data.stats.done ? " · " + t("wb_p90_donen", { n: data.stats.done }) : "" }) }}</div>
          <div v-if="(data.stats || {}).client" class="wb-p90you">{{ t("wb_p90_yours", { y: data.stats.client }) }}</div>
          <template v-if="data.state === 'proposed'">
            <div class="wb-p90" :class="{ 'p90-edit': p90.edit }">
              <div class="wb-p90hint">{{ t(p90.edit ? "wb_p90_editing" : "wb_p90_hint") }}</div>
              <details v-for="(ph, i) in data.phases || []" :key="i" class="wb-ph" :open="i === firstPhase">
                <summary><div class="wb-pht">{{ ph.title }}<span v-if="(ph.tasks || []).length" class="wb-phn">{{ ph.tasks.length }} {{ t("wb_p90_moves") }}</span></div><div class="wb-phs">{{ ph.subtitle }}</div></summary>
                <div class="wb-phb">
                  <template v-if="(ph.tasks || []).length">
                    <TaskCard v-for="task in ph.tasks" :key="task.id" :task="task" :ctx="{ metric: true, drop: true, noacts: true, output: true }" :dropped="p90.drop[task.id]" @act="onTaskAct" />
                  </template>
                  <div v-else class="wb-phs">{{ ph.empty || "" }}</div>
                </div>
              </details>
            </div>
          </template>
          <template v-else>
            <div class="wb-p90"><div class="wb-sech">{{ t("wb_p90_live", { b: data.week_now || 1 }) }}</div>
              <div v-for="(ph, i) in data.phases || []" :key="i" style="margin-bottom:11px">
                <template v-if="(ph.tasks || []).length">
                  <div class="wb-phs"><b style="color:var(--ink)">{{ ph.title }}</b> · {{ doneOf(ph) }}/{{ ph.tasks.length }}</div>
                  <div class="wb-phbar"><i :style="{ width: Math.round(doneOf(ph) * 100 / ph.tasks.length) + '%' }"></i></div>
                </template>
              </div>
            </div>
            <div v-if="p90Wait.length" class="wb-sec"><div class="wb-sech warn">{{ t("wb_p90_waiting") }} ({{ p90Wait.length }})</div>
              <TaskCard v-for="task in p90Wait" :key="task.id" :task="task" :ctx="{ metric: true, output: true }" @act="onTaskAct" /></div>
            <div class="wb-sec"><div class="wb-sech">{{ t("wb_p90_thisweek") }} ({{ p90Week.length }})</div>
              <TaskCard v-for="task in p90Week" :key="task.id" :task="task" :ctx="{ metric: true, output: true }" @act="onTaskAct" /></div>
            <div v-if="p90Later.length" class="wb-sec"><div class="wb-sech">{{ t("wb_p90_later") }} ({{ p90Later.length }})</div>
              <TaskCard v-for="task in p90Later" :key="task.id" :task="task" :ctx="{ metric: true, output: true }" @act="onTaskAct" /></div>
            <div v-if="p90Done.length" class="wb-sec"><div class="wb-sech">{{ t("wb_p90_done") }} ({{ p90Done.length }})</div>
              <TaskCard v-for="task in p90Done" :key="task.id" :task="task" :ctx="{ metric: true, output: true, review: true }" @act="onTaskAct" /></div>
          </template>
        </template>
      </template>

      <!-- task: guided steps (or the look-back of a verified one) -->
      <template v-else-if="wb.type === 'task' && data">
        <template v-if="data.review">
          <div class="wbr-row"><h5>{{ (data.review.labels || {}).h || "" }}</h5><p class="big">{{ data.review.title || "" }}</p>
            <p v-if="data.review.why">{{ data.review.why }}</p></div>
          <div class="wbr-row"><h5>{{ (data.review.labels || {}).made || "" }}</h5>
            <template v-if="(data.review.delivs || []).length">
              <div v-for="(x, i) in data.review.delivs" :key="i" class="wbr-d">
                <div><b>{{ x.title || "" }}</b><span>{{ x.kind || "" }}</span></div>
                <button type="button" class="wbr-go" @click="emit('open', { type: 'deliverable', slug: x.slug })">{{ t("wbr_open") }}</button>
              </div>
              <p v-if="data.review.match === 'window'" class="wbr-hint">{{ (data.review.labels || {}).guess || "" }}</p>
            </template>
            <p v-else>{{ (data.review.labels || {}).nothing || t("wbr_none") }}</p>
          </div>
          <div v-if="data.review.when" class="wbr-row"><h5>{{ (data.review.labels || {}).when || "" }}</h5><p>{{ data.review.when }}</p></div>
          <div class="wbr-row"><h5>{{ (data.review.labels || {}).effect || "" }}</h5><p>{{ data.review.effect || "" }}</p></div>
        </template>
        <template v-else>
          <div v-if="taskAll" class="wbt-hero"><span class="wbt-stamp">{{ t("wbt_stamp") }}</span><b>{{ data.done_title || "" }}</b><span>{{ data.done_note || "" }}</span></div>
          <div v-if="data.intro" class="wbt-intro">{{ data.intro }}</div>
          <div v-if="data.output" class="wbt-out"><b>{{ t("wbt_out_h") }}</b>
            <div><span>{{ t("wbt_out_1") }}</span>{{ data.output.what }}</div>
            <div><span>{{ t("wbt_out_2") }}</span>{{ data.output.approve }}</div>
            <div><span>{{ t("wbt_out_3") }}</span>{{ data.output.where }}</div></div>
          <div class="wbt-meta"><span v-if="data.meta">{{ data.meta }}</span>
            <span v-if="data.total" class="wbt-prog">{{ t("wbt_prog", { b: data.done_n || 0, x: data.total }) }}</span></div>
          <div v-for="(it, i) in data.items || []" :key="i" class="wbt-card">
            <b class="wbt-n">{{ it.n || "" }}</b><span v-if="it.why" class="wbt-why">{{ it.why }}</span>
            <ul class="wbt-steps">
              <li v-for="(s, j) in it.steps || []" :key="j"><label class="wbt-step" :class="{ on: stepOn(i, j) }">
                <input type="checkbox" :checked="stepOn(i, j)" @change="toggleStep(i, j)"><span v-html="s"></span></label></li>
            </ul>
            <div v-if="it.paste" class="wbt-slip"><span class="wbt-sl">{{ it.paste_label || "" }}</span>
              <div class="wbt-pt">{{ it.paste }}</div>
              <button type="button" class="wbt-cpy" @click="copySlip(it.paste, $event)">{{ t("wbt_copy") }}</button></div>
            <div v-if="it.wb" class="wbt-acts"><button type="button" class="wbt-go" @click="emit('open', it.wb)">{{ it.url_label || "" }}</button></div>
            <div v-else-if="it.url" class="wbt-acts"><a class="wbt-go" :href="it.url" target="_blank" rel="noopener">{{ it.url_label || "" }} ↗</a><span class="wbt-after">{{ t("wbt_after") }}</span></div>
          </div>
          <div v-if="!(data.items || []).length" class="wb-empty"><b>{{ t("wbt_none") }}</b></div>
          <div v-if="data.is_material" class="wbt-box">
            <h4>{{ t("wbt_paste_h") }}</h4>
            <textarea v-model="material" :placeholder="t('wbt_paste_ph')"></textarea>
            <div class="wbt-boxact">
              <button type="button" class="mc-cta" :disabled="materialBusy" @click="submitMaterial">{{ materialBusy ? t("wbt_sending") : t("wbt_send") }}</button>
              <span class="wbt-note" :class="{ warn: materialNoteWarn }">{{ materialNote }}</span>
            </div>
          </div>
        </template>
      </template>

      <!-- unknown type: show the payload instead of failing silently -->
      <pre v-else class="wb-json">{{ JSON.stringify(wb, null, 2) }}</pre>
    </div>

    <div v-if="hasFoot" class="wb-foot">
      <template v-if="wb.type === 'deliverable' && data">
        <button type="button" class="mc-ghost" @click="copyBody($event)">{{ t("wb_copy") }}</button>
        <button v-if="data.published" type="button" class="mc-ghost" @click="emit('open', { type: 'site', slug: data.slug })">{{ t("wb_preview") }}</button>
        <button v-if="(data.kind || '') === 'article'" type="button" class="mc-cta" :disabled="pubBusy" @click="publish(false)">{{ t(data.published ? "art_republish" : "art_publish") }}</button>
        <div v-if="unconf" class="art-unconf" role="alert">
          <b>{{ t("art_unconf_h") }}</b> {{ t("art_unconf_s", { n: unconf.needs_confirm || 0 }) }}
          <button type="button" class="mc-cta" @click="publish(true)">{{ t("art_unconf_force") }}</button>
        </div>
      </template>
      <template v-else-if="wb.type === 'report' && data">
        <button v-if="data.report_url" type="button" class="mc-ghost" @click="openUrl(data.report_url)">{{ t("wb_rep_orig") }}</button>
        <button v-if="data.slug" type="button" class="mc-ghost" :disabled="pdfBusy" @click="downloadPdf">{{ pdfBusy ? t("wb_rep_pdfbusy") : t("wb_rep_pdf") }}</button>
        <button type="button" class="mc-cta" @click="emit('open', { type: 'plan90', brand: data.brand || brand })">{{ data.plan_cta || t("wb_rep_plan") }}</button>
      </template>
      <template v-else-if="wb.type === 'plan90' && data && (data.stats || {}).total">
        <template v-if="data.state === 'proposed'">
          <button type="button" class="mc-cta" @click="p90Build">{{ p90BuildLabel }}</button>
          <button type="button" class="mc-ghost" :disabled="p90Busy" @click="approveP90">{{ t("wb_p90_approve") }}</button>
          <button type="button" class="mc-ghost" @click="p90.edit = !p90.edit">{{ t(p90.edit ? "wb_p90_editdone" : "wb_p90_edit") }}</button>
        </template>
        <button v-else type="button" class="mc-ghost" @click="emit('open', { type: 'report', brand: data.brand || brand, sec: 'overview' })">{{ t("wb_p90_report") }}</button>
      </template>
      <template v-else-if="wb.type === 'task' && data && !data.review">
        <button v-if="!taskAll && !data.is_material && (data.items || []).length" type="button" class="mc-cta" @click="finishAll">{{ t("wbt_alldone") }}</button>
        <a v-if="data.share_url" class="mc-ghost" :href="data.share_url" target="_blank" rel="noopener">{{ t("wbt_share") }}</a>
      </template>
      <template v-else-if="wb.type === 'task' && data && data.review && data.share_url">
        <a class="mc-ghost" :href="data.share_url" target="_blank" rel="noopener">{{ t("wbt_share") }}</a>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";

import { api } from "../api/client";
import { getDeliverable, publishDeliverable } from "../api/deliverables";
import { approvePlan, provideTask, setTaskStatus } from "../api/plans";
import { celebrateGuide, getTaskGuide, setGuideStep, submitGuide } from "../api/taskGuides";
import { blockNode, valueText } from "../legacy/mayaBlocks";
import { mdLite } from "../legacy/mayaChat";
import { soaToast } from "../legacy/soaToast";
import { session } from "../stores/session";
import {
  onPlanExecutionCompleted,
  planExecutionFailureForTask,
  planExecutionForTask,
  submitPlanExecution,
} from "../stores/planExecutions.js";
import { useI18n } from "../useI18n";

const props = defineProps({
  wb: { type: Object, required: true },
  brand: { type: String, default: "" },
  brandId: { type: [Number, String], default: null },
});
const emit = defineEmits(["open", "meta", "bubble", "say", "ask", "upgrade", "close"]);
const { t, lang } = useI18n();

const data = ref(null);
const busy = ref(false);
const failed = ref(false);
const sec = ref("");
const pdfBusy = ref(false);
const pubBusy = ref(false);
const unconf = ref(null);
const planBusy = ref(false);
const p90Busy = ref(false);
const p90 = reactive({ drop: {}, edit: false });
const material = ref("");
const materialNote = ref("");
const materialNoteWarn = ref(false);
const materialBusy = ref(false);
const sitesMap = ref({});
const startingExecutionTaskId = ref(null);
let unsubscribeExecution;

// wraps the shared block renderer so it can live in the template and emit section switches
const BlockView = defineComponent({
  name: "BlockView",
  props: { bl: { type: Object, default: null } },
  emits: ["section"],
  setup(p, { emit: blockEmit }) { return () => blockNode(p.bl, "blk", (s) => blockEmit("section", s)); },
});

// plTaskCard, ported to vnodes. `ctx` mirrors the old options (metric/drop/noacts/output/review);
// `dropped` carries the plan90 edit-mode un-tick so nothing is written server-side before approve.
const TaskCard = defineComponent({
  name: "WbTaskCard",
  props: { task: { type: Object, required: true }, ctx: { type: Object, default: () => ({}) }, dropped: { type: Boolean, default: false } },
  emits: ["act"],
  setup(p, { emit: actEmit }) {
    const zh = computed(() => lang.value === "zh");
    return () => {
      const x = p.task, opt = p.ctx || {};
      const title = zh.value && x.title_zh ? x.title_zh : x.title;
      const chip = h("span", { class: ["pl-chip", x.owner === "maya" ? "pl-m" : "pl-c"] }, x.owner === "maya" ? "Maya" : (zh.value ? "需要你" : "You"));
      let btn = "";
      const site = sitesMap.value[String(x.brand || "").toLowerCase()] || null;
      const isBld = x.action_id === "site_build" || x.kind === "build";
      const execution = planExecutionForTask(x.id);
      const failure = planExecutionFailureForTask(x.id);
      const starting = String(startingExecutionTaskId.value || "") === String(x.id);
      const active = Boolean(execution) || starting;
      const effectiveStatus = active && x.owner === "maya"
        ? "active"
        : failure && x.status === "active"
          ? (["proposed", "planned"].includes(failure.origin_status) ? failure.origin_status : x.status)
          : x.status;
      if (opt.noacts) btn = "";
      // IMP-AA2: a site-build task whose site already exists swaps the CTA — published → view live +
      // edit, draft → open the workbench. Never "she'll auto-do it" against a site that's standing.
      else if (isBld && site && site.status !== "none" && !active) {
        btn = [
          site.status === "published" && site.live_url ? h("a", { class: "pl-go ghost", href: site.live_url, target: "_blank", rel: "noopener" }, zh.value ? "看线上站 ↗" : "View live ↗") : null,
          h("button", { type: "button", class: "pl-go", onClick: () => actEmit("act", { kind: "sitewb", id: site.brand_id }) }, zh.value
            ? (site.status === "published" ? "修改 GEO 站" : "打开工作台")
            : (site.status === "published" ? "Edit site" : "Open workbench")),
        ];
      }
      else if (x.owner === "maya" && (active || ["proposed", "planned"].includes(effectiveStatus))) {
        const label = !execution || execution.state === "queued" ? t("plan_queued") : t("plan_running");
        btn = h("button", {
          type: "button",
          class: "pl-go",
          disabled: active,
          "aria-busy": active ? "true" : undefined,
          onClick: active ? undefined : () => actEmit("act", { kind: "exec", id: x.id }),
        }, active ? label : failure ? t("plan_retry") : (zh.value ? "现在做 ›" : t("plan_do_now")));
      }
      if (x.owner === "client" && x.status !== "done" && x.status !== "verifying") {
        btn = h("button", { type: "button", class: "pl-go", onClick: () => actEmit("act", { kind: "provide", id: x.id }) }, zh.value ? "去提供 ›" : "Provide it ›");
      }
      let mark = null;
      if (!opt.noacts) {
        if (effectiveStatus === "done") {
          mark = x.deliv_slug
            ? h("button", { type: "button", class: "pl-donebadge pl-rev", onClick: () => actEmit("act", { kind: "deliverable", slug: x.deliv_slug }) }, "✓ " + (zh.value ? "已核验 · " : "Verified · ") + valueText(title))
            : opt.review
            ? h("button", { type: "button", class: "pl-donebadge pl-rev", onClick: () => actEmit("act", { kind: "review", id: x.id }) }, "✓ " + (zh.value ? "已核验 · 看看做了什么" : "Verified · see what she did"))
            : h("span", { class: "pl-donebadge" }, "✓ " + (zh.value ? "已核验" : "Verified"));
        } else if (failure) mark = h("span", { class: "pl-failed", role: "alert" }, failure.reason || failure.message || t("plan_execute_error"));
        else if (effectiveStatus === "verifying") mark = h("span", { class: "pl-verify" }, "✅ " + (zh.value ? "你已完成 · Maya 核验中" : "You did it · Maya’s verifying"));
        else if (active && x.owner === "maya") {
          const label = !execution || execution.state === "queued" ? t("plan_queued") : t("plan_running");
          mark = h("span", { class: "pl-working", role: "status", "aria-live": "polite", "aria-label": label }, label);
        }
      }
      const why = zh.value && x.why_zh ? x.why_zh : (x.why || "");
      const acts = [opt.metric && x.metric_label ? h("span", { class: "pl-metric" }, [h("i", "↗"), valueText(x.metric_label)]) : null, btn, mark,
        opt.drop ? h("button", { type: "button", class: "pl-drop", onClick: () => actEmit("act", { kind: "drop", id: x.id }) }, valueText(t(p.dropped ? "wb_p90_undrop" : "wb_p90_drop"))) : null];
      return h("div", { class: ["pl-card", effectiveStatus === "done" ? "isdone" : "", active ? "is-active" : "", p.dropped ? "p90-off" : ""], "aria-busy": active ? "true" : "false" }, [
        h("div", { class: "pl-top" }, [h("span", { class: "pl-wk" }, "W" + valueText(x.week)), h("b", valueText(title)), chip, h("span", { class: "pl-brand" }, valueText(x.brand))]),
        h("p", { class: "pl-why" }, valueText(why)),
        opt.output && x.output && x.output.line ? h("div", { class: "pl-out" }, valueText(x.output.line)) : null,
        h("div", { class: "pl-acts" }, acts.filter(Boolean)),
      ]);
    };
  },
});

const bodyHtml = computed(() => (data.value?.html) || mdLite(data.value?.body || ""));
const firstPhase = computed(() => { const ph = (data.value?.phases) || []; for (let i = 0; i < ph.length; i++) if ((ph[i].tasks || []).length) return i; return -1; });
const allP90 = computed(() => { const out = []; ((data.value?.phases) || []).forEach((ph) => ((ph.tasks) || []).forEach((x) => out.push(x))); return out; });
function doneOf(ph) { return ((ph.tasks) || []).filter((x) => x.status === "done").length; }
const p90Wait = computed(() => allP90.value.filter((x) => x.owner === "client" && x.status !== "done" && x.status !== "verifying"));
const p90Week = computed(() => { const wk = data.value?.week_now || 1; return allP90.value.filter((x) => x.owner !== "client" && x.status !== "done" && x.week <= wk); });
const p90Later = computed(() => { const wk = data.value?.week_now || 1; return allP90.value.filter((x) => x.owner !== "client" && x.status !== "done" && x.week > wk); });
const p90Done = computed(() => allP90.value.filter((x) => x.status === "done"));
const planTasks = computed(() => ((data.value?.tasks) || []));
const planMine = computed(() => planTasks.value.filter((x) => x.owner === "maya" && x.status !== "done" && x.status !== "dropped"));
const planYours = computed(() => planTasks.value.filter((x) => x.owner === "client" && x.status !== "done" && x.status !== "dropped"));
const planProposed = computed(() => planTasks.value.filter((x) => x.status === "proposed"));
const planWeek = computed(() => { const wk = data.value?.week_now || 1; return planTasks.value.filter((x) => x.status !== "proposed" && x.status !== "dropped" && x.status !== "done" && x.week <= wk); });
const planLater = computed(() => { const wk = data.value?.week_now || 1; return planTasks.value.filter((x) => x.status !== "proposed" && x.status !== "dropped" && x.status !== "done" && x.week > wk); });
const planDone = computed(() => planTasks.value.filter((x) => x.status === "done"));
const taskAll = computed(() => { const d = data.value; if (!d) return false; return d.status === "done" || !!d.celebrated; });
function stepOn(i, j) { const st = data.value?.steps_done || {}; return !!st[i + "-" + j]; }
const hasFoot = computed(() => {
  const ty = props.wb.type, d = data.value;
  if (ty === "deliverable") return Boolean(d);
  if (ty === "report") return Boolean(d);
  if (ty === "plan90") return Boolean(d && (d.stats || {}).total);
  if (ty === "task") return Boolean(d && (d.share_url || (!d.review && !taskAll.value && (d.items || []).length)));
  return false;
});
const p90BuildLabel = computed(() => {
  const cs = sitesMap.value[String(data.value?.brand || props.brand || "").toLowerCase()] || null;
  if (cs && cs.status === "published") return t("wb_p90_edit_site");
  if (cs && cs.status === "draft") return t("wb_p90_open_wb");
  return t("wb_p90_build");
});

function openUrl(url) { if (url) window.open(String(url), "_blank", "noopener"); }
function openSec(s) { if (!s || s === sec.value) return; sec.value = s; loadReport(); }

async function run(loader) {
  busy.value = true; failed.value = false;
  try { await loader(); } catch { failed.value = true; } finally { busy.value = false; }
}

function load() {
  const wb = props.wb;
  data.value = wb.html && wb.type === "deliverable" ? { ...wb } : null;   // preloaded body shows immediately
  if (wb.type === "compare") return;
  if (wb.type === "deliverable") return run(async () => {
    if (!wb.slug) return;
    const d = await getDeliverable(wb.slug);
    if (!d || !d.slug) { if (!wb.html) failed.value = true; return; }
    data.value = d;
    emit("meta", { title: d.title || wb.title || "", kind: d.kind || "" });
  });
  if (wb.type === "site") return run(async () => {
    const d = await getDeliverable(wb.slug);
    if (!d || !d.slug) { failed.value = true; return; }
    data.value = d; emit("meta", { title: d.title || t("wb_preview"), kind: t("wb_kind_site") });
  });
  if (wb.type === "report") { sec.value = wb.sec || "overview"; return loadReport(); }
  if (wb.type === "plan") return run(async () => {
    const d = await api("/api/plan");
    data.value = { tasks: (d && d.tasks) || [], week_now: (d && d.week_now) || 1 };
    warmSites();
  });
  if (wb.type === "plan90") return run(async () => {
    const bn = wb.brand || props.brand || "";
    const d = await api("/api/plan90" + (bn ? "?brand=" + encodeURIComponent(bn) : ""));
    if (!d || !d.ok) { data.value = { stats: {} }; return; }
    data.value = d; p90.drop = {}; p90.edit = false;
    emit("meta", { title: (d.brand ? d.brand + " · " : "") + t("wb_p90_title") });
    warmSites();
  });
  if (wb.type === "task") return run(async () => {
    const d = await getTaskGuide(wb.id);
    if (!d || !d.ok) { failed.value = true; return; }
    data.value = d;
    emit("meta", { title: d.title || "", kind: d.review ? "wbr_kind" : "" });
  });
}

async function loadReport() {
  const wb = props.wb;
  await run(async () => {
    const d = await api(`/api/report-section?sec=${encodeURIComponent(sec.value)}&brand=${encodeURIComponent(wb.brand || props.brand || "")}`);
    if (!d || !d.ok) { data.value = { secs: [], blocks: [] }; return; }
    data.value = d;
    emit("meta", { title: d.brand ? d.brand + " · " + d.title : d.title });
  });
}

// /api/sites warms the state-aware build CTAs (same cache siteCtaFor read in the old app)
function warmSites() {
  api("/api/sites").then((d) => {
    const map = {};
    ((d && d.sites) || []).forEach((s) => { map[String(s.brand || "").toLowerCase()] = { status: s.status, brand_id: s.brand_id, live_url: s.live_url || "" }; });
    sitesMap.value = map;
  }).catch(() => {});
}

async function onTaskAct(act) {
  if (act.kind === "drop") { p90.drop[act.id] = !p90.drop[act.id]; return; }
  if (act.kind === "review") { emit("open", { type: "task", id: act.id }); return; }
  if (act.kind === "deliverable") { emit("open", { type: "deliverable", slug: act.slug }); return; }
  if (act.kind === "sitewb") { emit("open", { type: "sitewb", brand_id: act.id }); return; }
  if (act.kind === "exec") {
    if (String(startingExecutionTaskId.value || "") === String(act.id)) return;
    startingExecutionTaskId.value = String(act.id);
    try {
      await submitPlanExecution(act.id);
      soaToast(t("plan_started"), "", "ok");
    } catch (error) {
      soaToast(error?.message || t("plan_execute_error"), "", "warn");
    } finally {
      if (String(startingExecutionTaskId.value || "") === String(act.id)) startingExecutionTaskId.value = null;
    }
    return;
  }
  if (act.kind === "provide") {
    try {
      const d = await provideTask(act.id, session.language);
      if (!(d && d.ok)) { soaToast((d && d.error) || "failed", "", "warn"); return; }
      if (!d.task_wb) emit("close");
      emit("bubble", { role: "assistant", content: d.msg || "", action: d.action || null });
      if (d.task_wb) emit("open", { type: "task", id: act.id });
    } catch { soaToast("failed", "", "warn"); }
  }
}

async function refreshPlanSurface() {
  if (props.wb.type === "plan") {
    const result = await api("/api/plan");
    data.value = { tasks: result?.tasks || [], week_now: result?.week_now || 1 };
    return;
  }
  if (props.wb.type === "plan90") {
    const brand = props.wb.brand || props.brand || "";
    const result = await api("/api/plan90" + (brand ? "?brand=" + encodeURIComponent(brand) : ""));
    if (result?.ok) data.value = result;
  }
}

async function approveAll() {
  planBusy.value = true;
  try {
    await approvePlan(props.brandId ?? undefined);
    soaToast(t("wb_p90_toast"), "", "ok");
    emit("say", t("wb_said_approved"));
    await run(async () => { const d = await api("/api/plan"); data.value = { tasks: (d && d.tasks) || [], week_now: (d && d.week_now) || 1 }; });
  } catch { soaToast("failed", "", "warn"); } finally { planBusy.value = false; }
}

async function approveP90() {
  p90Busy.value = true;
  const drop = Object.keys(p90.drop).filter((k) => p90.drop[k]).map((k) => parseInt(k, 10));
  try {
    const r = await api("/api/plan90/approve", { method: "POST", json: { brand: data.value?.brand || props.brand || "", drop } });
    if (r && r.upgrade) { p90Busy.value = false; emit("upgrade", r.msg, r.url); return; }
    if (!(r && r.ok)) { soaToast((r && r.error) || "failed", "", "warn"); return; }
    soaToast(t("wb_p90_toast"), "", "ok");
    emit("say", t("wb_said_approved"));
    await run(async () => {
      const bn = props.wb.brand || props.brand || "";
      const d = await api("/api/plan90" + (bn ? "?brand=" + encodeURIComponent(bn) : ""));
      if (d && d.ok) { data.value = d; p90.drop = {}; p90.edit = false; }
    });
  } catch { soaToast("failed", "", "warn"); } finally { p90Busy.value = false; }
}

function p90Build() {
  const cs = sitesMap.value[String(data.value?.brand || props.brand || "").toLowerCase()] || null;
  if (cs && cs.status !== "none" && cs.brand_id != null) { emit("open", { type: "sitewb", brand_id: cs.brand_id }); return; }
  const bn = data.value?.brand || props.brand || "";
  emit("ask", bn ? t("maya_build_q", { b: bn }) : t("maya_build_q0"));
}

async function toggleStep(i, j) {
  const d = data.value; if (!d) return;
  const key = i + "-" + j, want = !stepOn(i, j);
  const steps = { ...(d.steps_done || {}) };
  if (want) steps[key] = true; else delete steps[key];
  d.steps_done = steps;   // optimistic
  try {
    const r = await setGuideStep(d.task_id, key, want);
    if (!(r && r.ok)) throw new Error("step");
    d.steps_done = r.steps_done || steps; d.done_n = r.done_n ?? d.done_n; d.total = r.total ?? d.total;
  } catch {
    const revert = { ...(d.steps_done || {}) };
    if (want) delete revert[key]; else revert[key] = true;
    d.steps_done = revert;
    soaToast(t("wbt_err"), "", "warn");
  }
}

function taskFinish(r) {
  const d = data.value; if (!d) return;
  d.status = "done";
  if (r && r.celebrate && r.msg) emit("bubble", { role: "assistant", content: r.msg, action: r.action || null });
}

async function finishAll() {
  const d = data.value; if (!d) return;
  try {
    await setTaskStatus(d.task_id, "done");
    const r = await celebrateGuide(d.task_id);
    d.celebrated = true;
    taskFinish(r);
  } catch { soaToast(t("wbt_err"), "", "warn"); }
}

async function submitMaterial() {
  const d = data.value; if (!d) return;
  const txt = (material.value || "").trim();
  if (txt.length < 8) { materialNote.value = t("wbt_short"); materialNoteWarn.value = true; return; }
  materialBusy.value = true; materialNote.value = ""; materialNoteWarn.value = false;
  try {
    const r = await submitGuide(d.task_id, txt);
    if (!(r && r.ok)) { materialNote.value = (r && r.error) || t("wbt_err"); materialNoteWarn.value = true; return; }
    emit("bubble", { role: "user", content: txt });
    d.status = r.status || "done"; d.is_material = false;
    taskFinish(r);
  } catch { materialNote.value = t("wbt_err"); materialNoteWarn.value = true; } finally { materialBusy.value = false; }
}

function copyText(txt) { try { navigator.clipboard?.writeText(String(txt)); } catch { /* optional */ } }
// the pre-Vue feedback: the button itself confirms the copy, then returns to its label
function swapLabel(event, okKey, baseKey) {
  const b = event?.currentTarget || event?.target;
  if (b?.textContent == null) return;
  const old = b.textContent;
  b.textContent = t(okKey);
  setTimeout(() => { b.textContent = t(baseKey); }, 1400);
}
function copyBody(event) { copyText(data.value?.body || ""); swapLabel(event, "ob_copied", "wb_copy"); }
function copySlip(txt, event) { copyText(txt); swapLabel(event, "wbt_copied", "wbt_copy"); }

// the same report, as a file she can keep: blob hand-off, never a plain href
async function downloadPdf() {
  const d = data.value;
  if (!d?.slug || pdfBusy.value) return;
  pdfBusy.value = true;
  try {
    const j = await api(`/api/report/${encodeURIComponent(d.slug)}.pdf`);
    if (!(j && j.ok && j.url)) throw new Error("build");
    const res = await fetch(j.url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("fetch");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = j.filename || "SoaRank-report.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch { soaToast(t("wb_rep_pdferr"), "", "warn"); } finally { pdfBusy.value = false; }
}

async function publish(force) {
  const d = data.value;
  if (!d?.slug || pubBusy.value) return;
  const wasPub = Boolean(d.published);
  pubBusy.value = true;
  unconf.value = null;
  try {
    const r = await publishDeliverable(d.slug, force);
    if (r && r.ok) {
      d.published = true; d.live_url = r.url || d.live_url; d.live_path = (r.url && String(r.url).indexOf("http") !== 0) ? r.url : d.live_path;
      soaToast(t(wasPub ? "art_republished" : "art_live_ok"), r.url || "", "ok");
      emit("say", t(wasPub ? "wb_said_republished" : "wb_said_published"));
      if (r.card) emit("bubble", { role: "assistant", content: r.card.title || "", action: { card: r.card } });
    } else if (r && r.upgrade) emit("upgrade", r.msg, r.url);
    // material gate: same inline confirm the article card uses — never a window.confirm
    else if (r && r.error === "unconfirmed") unconf.value = r;
    else soaToast((r && (r.msg || r.error)) || t("maya_art_write_fail"), "", "warn");
  } catch (e) { soaToast(e?.message || t("maya_art_write_fail"), "", "warn"); } finally { pubBusy.value = false; }
}

// Re-load when the drawer is pointed at something else. Payload identity covers same-type re-opens
// (plan → plan) that no id field would catch; meta-only updates (title/kind from a finished load)
// replace the object too, so compare on the identity fields before deciding.
let lastSig = "";
function payloadSig(wb) { return [wb?.type ?? "", wb?.slug ?? "", wb?.brand ?? "", wb?.id ?? "", props.brandId ?? ""].join("|"); }
watch(() => props.wb, (nw) => {
  if (!nw) return;
  const sig = payloadSig(nw);
  if (sig !== lastSig) { lastSig = sig; load(); }
});
onMounted(() => {
  lastSig = payloadSig(props.wb);
  load();
  unsubscribeExecution = onPlanExecutionCompleted(() => {
    void refreshPlanSurface().catch(() => {});
  });
});
onBeforeUnmount(() => { unsubscribeExecution?.(); });
</script>

<style scoped>
/* the drawer's body becomes a column so the foot pins below the scroll area, like the old .wb */
.wb-stack { padding: 0; overflow: hidden; display: flex; flex-direction: column; }
.wb-scroll { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 18px 20px 26px; }
</style>
